import { create } from 'zustand';
import type { DesignConfig, LifestyleElement } from '../api/types';
import { getLifestyle } from '../api/types';
import { confineToProductHalf, disableLifestyle, enableLifestyle } from '../lib/lifestyleLayout';

type CommitOpts = { history?: boolean };

interface EditorState {
  config: DesignConfig | null;
  dirty: boolean;
  past: DesignConfig[];
  future: DesignConfig[];
  /** Elements as they stood just before lifestyle mode was switched on, so
   *  switching it off can be an exact inverse. Deliberately store state and NOT
   *  part of the config: anything living in config.elements feeds config_hash
   *  and would re-render the whole catalogue for a purely editorial undo. */
  preLifestyle: DesignConfig['elements'] | null;
  setConfig: (c: DesignConfig) => void;
  patchElement: (key: string, patch: Record<string, unknown>, opts?: CommitOpts) => void;
  /** Switch lifestyle mode ON: writes the element, hides the other elements and
   *  pulls any stray back inside the product half (see lib/lifestyleLayout). */
  enableLifestyleMode: (lifestyle: LifestyleElement) => void;
  /** Switch lifestyle mode OFF: drops the element and restores the overlays. */
  disableLifestyleMode: () => void;
  /** Patch the lifestyle element, re-confining the others when the half moves. */
  patchLifestyle: (patch: Partial<LifestyleElement>) => void;
  addCustomElement: (key: string, element: Record<string, unknown>) => void;
  removeElement: (key: string) => void;
  patchFilter: (filter: DesignConfig['filter']) => void;
  patchMeta: (
    meta: Partial<Pick<DesignConfig, 'name' | 'priority' | 'active_from' | 'active_to'>>,
  ) => void;
  patchAssets: (assets: Partial<DesignConfig['assets']>) => void;
  markClean: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

const HISTORY_LIMIT = 50;

/** Merge a patch into an element, treating an `undefined` patch value as "delete
 *  this key" rather than "set it to undefined". A plain `{ ...current, ...patch }`
 *  spread would leave the key present-but-undefined, which is NOT the same as an
 *  absent key: e.g. product_image's cutout tri-state uses `cutout: undefined` to
 *  mean "follow store default", and that has to be a truly absent key so
 *  pre-existing configs stay byte-identical (spec 2026-09-01 §1.1). */
function mergeElement(
  current: Record<string, unknown> | undefined,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...current, ...patch };
  for (const k of Object.keys(patch)) {
    if (patch[k] === undefined) delete next[k];
  }
  return next;
}

function pushHistory(past: DesignConfig[], snapshot: DesignConfig | null): DesignConfig[] {
  if (snapshot === null) return past;
  const next = [...past, snapshot];
  if (next.length > HISTORY_LIMIT) next.shift();
  return next;
}

export const useEditor = create<EditorState>((set, get) => ({
  config: null,
  dirty: false,
  past: [],
  future: [],
  preLifestyle: null,
  // A snapshot belongs to the config it was taken from; loading another one
  // must not let "go back to single layout" paste in a stranger's elements.
  setConfig: (config) =>
    set({ config, dirty: false, past: [], future: [], preLifestyle: null }),
  patchElement: (key, patch, opts) =>
    set((s) => {
      if (!s.config) return s;
      const elements = s.config.elements as unknown as Record<string, Record<string, unknown>>;
      let merged = {
        ...s.config.elements,
        [key]: mergeElement(elements[key], patch),
      } as DesignConfig['elements'];
      // Revealing an element that enableLifestyle hid would otherwise put it
      // back at its full-canvas coordinates — the logo returns to x=880, off a
      // 540px half, with no handle to grab. Clamp on the way back in.
      const ls = getLifestyle(merged);
      if (patch.visible === true && ls?.enabled) {
        merged = confineToProductHalf(merged, ls.ratio);
      }
      const next: DesignConfig = { ...s.config, elements: merged };
      const recordHistory = opts?.history !== false;
      return {
        config: next,
        dirty: true,
        past: recordHistory ? pushHistory(s.past, s.config) : s.past,
        future: recordHistory ? [] : s.future,
      };
    }),
  enableLifestyleMode: (lifestyle) =>
    set((s) => {
      if (!s.config) return s;
      return {
        config: { ...s.config, elements: enableLifestyle(s.config.elements, lifestyle) },
        dirty: true,
        past: pushHistory(s.past, s.config),
        future: [],
        preLifestyle: s.config.elements,
      };
    }),
  disableLifestyleMode: () =>
    set((s) => {
      if (!s.config || !getLifestyle(s.config.elements)) return s;
      return {
        config: {
          ...s.config,
          elements: disableLifestyle(s.config.elements, s.preLifestyle),
        },
        dirty: true,
        past: pushHistory(s.past, s.config),
        future: [],
        preLifestyle: null,
      };
    }),
  patchLifestyle: (patch) =>
    set((s) => {
      if (!s.config) return s;
      const current = getLifestyle(s.config.elements);
      if (!current) return s;
      const next = { ...current, ...patch };
      let elements = {
        ...s.config.elements,
        lifestyle: next,
      } as unknown as DesignConfig['elements'];
      // A ratio bump strands elements exactly the way switching the mode on
      // does, so the same clamp has to run.
      if (patch.ratio !== undefined && patch.ratio !== current.ratio) {
        elements = confineToProductHalf(elements, next.ratio);
      }
      return {
        config: { ...s.config, elements },
        dirty: true,
        past: pushHistory(s.past, s.config),
        future: [],
      };
    }),
  addCustomElement: (key, element) =>
    set((s) => {
      if (!s.config) return s;
      if (key in s.config.elements) {
        // refuse to overwrite an existing key — caller should pick a unique name
        return s;
      }
      const next: DesignConfig = {
        ...s.config,
        elements: {
          ...s.config.elements,
          [key]: element,
        } as DesignConfig['elements'],
      };
      return {
        config: next,
        dirty: true,
        past: pushHistory(s.past, s.config),
        future: [],
      };
    }),
  removeElement: (key) =>
    set((s) => {
      if (!s.config) return s;
      const elements = { ...s.config.elements } as Record<string, unknown>;
      if (!(key in elements)) return s;
      delete elements[key];
      const next: DesignConfig = {
        ...s.config,
        elements: elements as DesignConfig['elements'],
      };
      return {
        config: next,
        dirty: true,
        past: pushHistory(s.past, s.config),
        future: [],
      };
    }),
  patchFilter: (filter) =>
    set((s) =>
      s.config
        ? {
            config: { ...s.config, filter },
            dirty: true,
            past: pushHistory(s.past, s.config),
            future: [],
          }
        : s,
    ),
  patchMeta: (meta) =>
    set((s) =>
      s.config
        ? {
            config: { ...s.config, ...meta },
            dirty: true,
            past: pushHistory(s.past, s.config),
            future: [],
          }
        : s,
    ),
  patchAssets: (assets) =>
    set((s) =>
      s.config
        ? {
            config: { ...s.config, assets: { ...s.config.assets, ...assets } },
            dirty: true,
            past: pushHistory(s.past, s.config),
            future: [],
          }
        : s,
    ),
  markClean: () => set({ dirty: false }),
  undo: () =>
    set((s) => {
      if (s.past.length === 0 || !s.config) return s;
      const prev = s.past[s.past.length - 1];
      return {
        config: prev,
        dirty: true,
        past: s.past.slice(0, -1),
        future: [s.config, ...s.future],
      };
    }),
  redo: () =>
    set((s) => {
      if (s.future.length === 0 || !s.config) return s;
      const [next, ...rest] = s.future;
      return {
        config: next,
        dirty: true,
        past: [...s.past, s.config],
        future: rest,
      };
    }),
  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,
}));
